/**
 * What a discount covers: whole products, and single variants of products that are NOT whole.
 *
 * Stored as two lists on the discount document:
 *   itemIds         — the WHOLE product: every variant, including variants added to it later.
 *   subselectionIds — single variants (item.subselections[].id) of products that are not in itemIds.
 *
 * The one rule both discount pages must agree on is written here once: ticking every variant of a
 * product IS the whole product, so it is stored in itemIds. That keeps a variant added later covered, and
 * it is what a till on an older app understands — those read itemIds only, and simply give no discount on
 * a variant they cannot see, rather than on the whole product.
 *
 * A `selection` is { itemIds: [], subselectionIds: [] } and is changed IN PLACE, so a page can keep it in
 * data() and stay reactive.
 */
class DiscountSelectionUtil {

    /**
     * The variants that can be picked one by one. A variant saved without an id cannot be referenced from
     * a discount, so it is not offered — the product as a whole still is.
     */
    variantsOf(item){
        return Array.isArray(item.subselections) ? item.subselections.filter(variant => variant && variant.id) : [];
    }

    isWhole(selection, item){
        return selection.itemIds.indexOf(item.id) > -1;
    }

    /** Ids of this product's variants the discount covers — all of them when the product is whole. */
    selectedVariantIdsOf(selection, item){
        const variants = this.variantsOf(item);
        if(this.isWhole(selection, item)){
            return variants.map(variant => variant.id);
        }
        return variants.filter(variant => selection.subselectionIds.indexOf(variant.id) > -1)
                       .map(variant => variant.id);
    }

    /** Some variants but not the whole product — what the product row shows as indeterminate. */
    isPartial(selection, item){
        return !this.isWhole(selection, item) && this.selectedVariantIdsOf(selection, item).length > 0;
    }

    isTouched(selection, item){
        return this.isWhole(selection, item) || this.isPartial(selection, item);
    }

    /** The product row: whole → nothing; partly or not at all → whole. The same as the sold-out page. */
    toggleItem(selection, item){
        const wasWhole = this.isWhole(selection, item);
        this.removeVariantsOf(selection, item);
        this.remove(selection.itemIds, item.id);
        if(!wasWhole){
            selection.itemIds.push(item.id);
        }
    }

    /** A variant row. Unticking one variant of a whole product leaves the others ticked. */
    toggleVariant(selection, item, variant){
        if(this.isWhole(selection, item)){
            this.remove(selection.itemIds, item.id);
            this.variantsOf(item)
                .filter(other => other.id !== variant.id)
                .forEach(other => selection.subselectionIds.push(other.id));
        } else if(selection.subselectionIds.indexOf(variant.id) > -1){
            this.remove(selection.subselectionIds, variant.id);
        } else {
            selection.subselectionIds.push(variant.id);
        }
        this.normalizeItem(selection, item);
    }

    /** Every variant ticked becomes the whole product; a whole product carries no single variants. */
    normalizeItem(selection, item){
        if(this.isWhole(selection, item)){
            this.removeVariantsOf(selection, item);
            return;
        }
        const variants = this.variantsOf(item);
        if(variants.length > 0 && variants.every(variant => selection.subselectionIds.indexOf(variant.id) > -1)){
            this.removeVariantsOf(selection, item);
            selection.itemIds.push(item.id);
        }
    }

    selectAll(selection, items){
        selection.itemIds.splice(0, selection.itemIds.length, ...items.map(item => item.id));
        selection.subselectionIds.splice(0, selection.subselectionIds.length);
    }

    clear(selection){
        selection.itemIds.splice(0, selection.itemIds.length);
        selection.subselectionIds.splice(0, selection.subselectionIds.length);
    }

    isAllSelected(selection, items){
        return items.length > 0 && items.every(item => this.isWhole(selection, item));
    }

    /**
     * A selection read from a stored discount, checked against the products that exist now.
     *
     * Anything it names that is no longer there — a deleted product, a variant removed from its product —
     * is counted rather than kept, so the page can SAY it will be dropped instead of losing it silently.
     */
    fromDiscount(discount, items){
        const itemsById = new Map(items.map(item => [item.id, item]));
        const variantOwner = new Map();
        items.forEach(item => this.variantsOf(item).forEach(variant => variantOwner.set(variant.id, item)));

        const selection = { itemIds: [], subselectionIds: [] };
        let missingCount = 0;
        (Array.isArray(discount.itemIds) ? discount.itemIds : []).forEach(id => {
            if(!itemsById.has(id)){
                missingCount++;
            } else if(selection.itemIds.indexOf(id) === -1){
                selection.itemIds.push(id);
            }
        });
        (Array.isArray(discount.subselectionIds) ? discount.subselectionIds : []).forEach(id => {
            if(!variantOwner.has(id)){
                missingCount++;
            } else if(selection.subselectionIds.indexOf(id) === -1){
                selection.subselectionIds.push(id);
            }
        });
        items.forEach(item => this.normalizeItem(selection, item));
        return { selection, missingCount };
    }

    copy(selection){
        return { itemIds: [...selection.itemIds], subselectionIds: [...selection.subselectionIds] };
    }

    removeVariantsOf(selection, item){
        const ids = this.variantsOf(item).map(variant => variant.id);
        for (let i = selection.subselectionIds.length - 1; i >= 0; i--) {
            if(ids.indexOf(selection.subselectionIds[i]) > -1){
                selection.subselectionIds.splice(i, 1);
            }
        }
    }

    remove(list, value){
        const index = list.indexOf(value);
        if(index > -1){
            list.splice(index, 1);
        }
    }
}

export default new DiscountSelectionUtil();
